import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import ArrowUpIcon from 'mdi-react/ArrowUpIcon';
import ArrowDownIcon from 'mdi-react/ArrowDownIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import FormModal from '../../../shared/components/custom/FormModal';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import EmbedPreview from './EmbedPreview';
import { partial } from '../../../util/translation/translation';

const p = partial('NewsForm');
const s = partial('shared');

// Text in these is code, not words on the page
const NOT_TEXT = 'style,script,noscript,template,title';
// Not sections of the page, wherever they are
const NOT_SECTION = new Set(['STYLE', 'SCRIPT', 'META', 'LINK', 'TEMPLATE']);
// The element a piece of text belongs to, and what it is to the editor
const BLOCKS =
  'h1,h2,h3,h4,h5,h6,p,li,a,button,td,th,figcaption,label,blockquote,dt,dd';
const kindOf = (block) => {
  if (!block) return 'text';
  const tag = block.tagName;
  if (/^H[1-6]$/.test(tag)) return 'heading';
  if (tag === 'BUTTON' || /\b(btn|button|cta)\b/i.test(block.className || '')) {
    return 'button';
  }
  if (tag === 'A') return 'link';
  if (tag === 'LI' || tag === 'DT' || tag === 'DD') return 'list';
  if (tag === 'TD' || tag === 'TH') return 'table';
  if (tag === 'FIGCAPTION' || tag === 'LABEL') return 'caption';
  return 'text';
};

const sectionsOf = (element) =>
  [...element.children].filter((child) => !NOT_SECTION.has(child.tagName));

/**
 * The page's sections: the parts inside its root (.club-page), or inside
 * the one wrapper the page may put around them all (looked through, a few
 * levels at most). Fewer than two: nothing to move or remove.
 */
const findSections = (content) => {
  let container = content.querySelector('.club-page');
  for (let depth = 0; container && depth < 4; depth += 1) {
    const sections = sectionsOf(container);
    if (sections.length >= 2) return { container, sections };
    [container] = sections.length === 1 ? sections : [null];
  }
  return { container: null, sections: [] };
};

/**
 * The page's words, each where it is: parsed as a fragment (a template, so
 * the viewport, style and the AI mark at the top stay where they are), and
 * every piece of text with what it belongs to. Pieces of one paragraph
 * (around a bold word or a link) are grouped, so they read in order, and
 * the groups go with the section they are in.
 */
const readPage = (html) => {
  const template = document.createElement('template');
  template.innerHTML = html || '';
  const { container, sections } = findSections(template.content);
  const walker = document.createTreeWalker(
    template.content,
    NodeFilter.SHOW_TEXT
  );
  const groups = [];
  let count = 0;
  let node = walker.nextNode();
  while (node) {
    const parent = node.parentElement;
    if (node.nodeValue.trim() && !parent?.closest(NOT_TEXT)) {
      const block = parent ? parent.closest(BLOCKS) : null;
      const last = groups[groups.length - 1];
      const piece = {
        node,
        // What's around the words (spaces, line breaks) is kept as it is
        lead: node.nodeValue.match(/^\s*/)[0],
        trail: node.nodeValue.match(/\s*$/)[0],
      };
      if (last && block && last.block === block) {
        last.pieces.push(piece);
      } else {
        groups.push({
          block,
          kind: kindOf(block),
          start: count,
          pieces: [piece],
          section: sections.findIndex((section) => section.contains(node)),
        });
      }
      count += 1;
    }
    node = walker.nextNode();
  }
  return {
    template,
    groups,
    container,
    // Where the sections sit among everything in their container, so they
    // go back into the same places in their new order
    slots: container ? [...container.childNodes] : [],
    sections: sections.map((element, index) => ({
      element,
      groups: groups.filter((group) => group.section === index),
      hasImage: !!element.querySelector('img,picture,svg,video'),
    })),
    // Text outside the sections (if any): edited, not moved
    loose: groups.filter((group) => group.section === -1),
  };
};

const rowsFor = (text) => Math.min(6, Math.max(1, Math.ceil(text.length / 60)));
const shorten = (text, length = 48) =>
  text.length > length ? `${text.slice(0, length - 1)}…` : text;
const range = (length) => Array.from({ length }, (_, at) => at);

/**
 * Change a page written with AI by hand: its words (a field per piece of
 * text) and its sections (moved up or down, or removed), with the page
 * beside them as it will look. Its layout, colours, images and links stay
 * as they are.
 */
const AiTextEditorModal = ({ isOpen, html, onClose, onSave }) => {
  const parsed = useMemo(
    () => (isOpen ? readPage(html) : null),
    [isOpen, html]
  );
  // The text of every piece, in order, as written and as it is now
  const [original, setOriginal] = useState([]);
  const [texts, setTexts] = useState([]);
  // The sections in their new order (by their place on the page), and
  // those taken out
  const [order, setOrder] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [preview, setPreview] = useState(html);

  const pieces = useMemo(
    () => (parsed ? parsed.groups.flatMap((group) => group.pieces) : []),
    [parsed]
  );

  // A newly opened page: as it is (pieces and html change only with it)
  useEffect(() => {
    if (!parsed) return;
    const start = pieces.map((piece) => piece.node.nodeValue.trim());
    setOriginal(start);
    setTexts(start);
    setOrder(range(parsed.sections.length));
    setRemoved([]);
    setPreview(html);
  }, [parsed, pieces, html]);

  // The page as it is now: the words written in, then the sections that
  // are kept put back in the places sections had, in their new order
  const write = (nextTexts, nextOrder, nextRemoved) => {
    pieces.forEach((piece, at) => {
      piece.node.nodeValue = `${piece.lead}${nextTexts[at]}${piece.trail}`;
    });
    const { container, slots, sections } = parsed;
    if (container) {
      const isSection = new Set(sections.map(({ element }) => element));
      const kept = nextOrder
        .filter((at) => !nextRemoved.includes(at))
        .map((at) => sections[at].element);
      let next = 0;
      const rebuilt = [];
      slots.forEach((slot) => {
        if (!isSection.has(slot)) {
          rebuilt.push(slot);
        } else if (next < kept.length) {
          rebuilt.push(kept[next]);
          next += 1;
        }
      });
      while (container.firstChild) container.removeChild(container.firstChild);
      rebuilt.forEach((child) => container.appendChild(child));
    }
    return parsed.template.innerHTML;
  };

  const update = ({
    nextTexts = texts,
    nextOrder = order,
    nextRemoved = removed,
  }) => {
    setTexts(nextTexts);
    setOrder(nextOrder);
    setRemoved(nextRemoved);
    setPreview(write(nextTexts, nextOrder, nextRemoved));
  };

  const setText = (at, value) => {
    const nextTexts = [...texts];
    nextTexts[at] = value;
    update({ nextTexts });
  };
  const move = (position, step) => {
    const nextOrder = [...order];
    [nextOrder[position], nextOrder[position + step]] = [
      nextOrder[position + step],
      nextOrder[position],
    ];
    update({ nextOrder });
  };
  const toggleRemoved = (section) =>
    update({
      nextRemoved: removed.includes(section)
        ? removed.filter((at) => at !== section)
        : [...removed, section],
    });

  // A section's name: its heading (as it reads now), else its first words,
  // else what it holds
  const groupText = (group) =>
    group.pieces
      .map((_, at) => texts[group.start + at] ?? '')
      .join(' ')
      .trim();
  const sectionLabel = (section, position) => {
    const named =
      section.groups.find((group) => group.kind === 'heading') ||
      section.groups[0];
    const text = named && groupText(named);
    if (text) return shorten(text);
    return section.hasImage
      ? p('aiSectionImage')
      : p('aiSection', { n: position + 1 });
  };

  const changed =
    texts.some((text, at) => text !== original[at]) ||
    removed.length > 0 ||
    order.some((section, position) => section !== position);
  const keptCount = order.length - removed.length;

  const renderGroup = (group) => (
    <div
      className={`ai-text-editor__group ai-text-editor__group--${group.kind}`}
      key={group.start}
    >
      <span className="ai-text-editor__kind">
        {p(`aiTextKind_${group.kind}`)}
      </span>
      {group.pieces.map((_, offset) => {
        const at = group.start + offset;
        const value = texts[at] ?? '';
        return (
          <textarea
            key={at}
            className="ai-text-editor__field"
            rows={rowsFor(value)}
            value={value}
            aria-label={p(`aiTextKind_${group.kind}`)}
            onChange={(event) => setText(at, event.target.value)}
          />
        );
      })}
    </div>
  );

  const empty =
    parsed && parsed.groups.length === 0 && parsed.sections.length === 0;
  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      className="ai-text-editor"
      title={p('aiTextEditTitle')}
      subtitle={p('aiTextEditHint')}
    >
      <div className="qm-body">
        {empty ? (
          <p className="mb-0">{p('aiTextEditEmpty')}</p>
        ) : (
          <div className="ai-text-editor__layout">
            <div className="ai-text-editor__fields">
              {parsed?.loose.map(renderGroup)}
              {order.map((sectionIndex, position) => {
                const section = parsed?.sections[sectionIndex];
                if (!section) return null;
                const label = sectionLabel(section, sectionIndex);
                if (removed.includes(sectionIndex)) {
                  return (
                    <div
                      key={sectionIndex}
                      className="ai-text-editor__section ai-text-editor__section--removed"
                    >
                      <span>{p('aiSectionRemoved', { section: label })}</span>
                      <button
                        type="button"
                        className="ai-text-editor__undo"
                        onClick={() => toggleRemoved(sectionIndex)}
                      >
                        {p('undo')}
                      </button>
                    </div>
                  );
                }
                return (
                  <section
                    key={sectionIndex}
                    className="ai-text-editor__section"
                    aria-label={label}
                  >
                    <div className="ai-text-editor__section-head">
                      <strong>{label}</strong>
                      <div className="icon-actions">
                        <SquareIconButton
                          icon={<ArrowUpIcon />}
                          title={p('aiSectionUp')}
                          disabled={position === 0}
                          onClick={() => move(position, -1)}
                        />
                        <SquareIconButton
                          icon={<ArrowDownIcon />}
                          title={p('aiSectionDown')}
                          disabled={position === order.length - 1}
                          onClick={() => move(position, 1)}
                        />
                        <SquareIconButton
                          icon={<DeleteOutlineIcon />}
                          title={p('aiSectionRemove')}
                          tone="danger"
                          // One part at least stays on the page
                          disabled={keptCount <= 1}
                          onClick={() => toggleRemoved(sectionIndex)}
                        />
                      </div>
                    </div>
                    {section.groups.length ? (
                      section.groups.map(renderGroup)
                    ) : (
                      <p className="ai-text-editor__no-text">
                        {p('aiSectionNoText')}
                      </p>
                    )}
                  </section>
                );
              })}
            </div>
            <div className="ai-text-editor__preview">
              <EmbedPreview html={preview} fullWidth />
            </div>
          </div>
        )}
      </div>
      <div className="qm-footer">
        <Button
          type="button"
          outline
          color="secondary"
          className="mb-0"
          onClick={onClose}
        >
          {s('cancel')}
        </Button>
        <Button
          type="button"
          color="primary"
          className="mb-0"
          disabled={!changed}
          onClick={() => onSave(preview)}
        >
          {p('aiTextEditSave')}
        </Button>
      </div>
    </FormModal>
  );
};

AiTextEditorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  html: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

AiTextEditorModal.defaultProps = {
  html: '',
};

export default AiTextEditorModal;
